import { useEffect, useRef, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { AsyncForm } from '@components/async-form';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { IcoCheck } from '@components/icons';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { showToast } from '@components/toaster';
import { useIntl } from 'shared/intl/use-intl';
import type { Checkin, PublicCheckinSettings } from 'server/dbx/checkins';
import { fmtTrackerDate } from 'shared/tracker-people';
import {
  TRACKER_BOX_CLASS,
  TRACKER_LINK_CLASS,
  TrackerGlance,
} from '@course/components/tracker/glance';

type Props = {
  courseId: UUID;
  settings: PublicCheckinSettings;
  localDate: string;
  isActiveDay: boolean;
  today?: Checkin;
  /** After every save, including the background draft autosave. */
  onSaved(checkin: Checkin): void;
  /**
   * Only after the student submits (Submit/Update standup). Anything that
   * should close or hide the form belongs here, not in onSaved: autosave fires
   * onSaved a moment after they stop typing, mid-answer.
   */
  onSubmitted?(checkin: Checkin): void;
  compact?: boolean;
  /** Show a one-line summary once this day's check-in is submitted; the student can expand it to edit. */
  collapseWhenSaved?: boolean;
  /**
   * The day this card represents, when it isn't today — a catch-up day
   * within the trailing window. Defaults to `localDate` (today) when
   * omitted, matching every existing caller.
   */
  date?: string;
  /** The tracker's numbers, for the "Since your last standup" box on today's card. */
  tracker?: TrackerGlance;
  /** The Tracker tab, linked from that box. */
  trackerHref?: string;
};

const AUTOSAVE_DEBOUNCE_MS = 1500;

function initialAnswers(settings: PublicCheckinSettings, existing?: Checkin) {
  const byPromptId: Record<string, string> = {};
  existing?.answers.forEach((a) => {
    if (a.promptId) {
      byPromptId[a.promptId] = a.answer;
    }
  });
  const result: Record<string, string> = {};
  settings.prompts.forEach((p) => {
    result[p.id] = byPromptId[p.id] || '';
  });
  return result;
}

function hasAnyText(answers: Record<string, string>) {
  return Object.values(answers).some((a) => a.trim());
}

const COPIED_MS = 2000;

/**
 * Today's tracker numbers above the questions: what was logged since the last
 * standup, or this week's counts before the first one.
 */
function TrackerSinceBox({
  tracker,
  trackerHref,
  onCopy,
}: {
  tracker: TrackerGlance;
  trackerHref?: string;
  onCopy?: (text: string) => void;
}) {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const { since } = tracker;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const boxClass = `${TRACKER_BOX_CLASS} mb-4`;

  if (since?.isEmpty) {
    const [before, link, after] = intl.split(
      'Since your last standup: nothing logged. Log touches on the <>Tracker tab</>.',
    );
    return (
      <p class={boxClass}>
        <span>
          {before}
          {trackerHref ? (
            <a class={TRACKER_LINK_CLASS} href={trackerHref}>
              {link}
            </a>
          ) : (
            link
          )}
          {after}
        </span>
      </p>
    );
  }

  const shown = since
    ? intl('Since your last standup ({day:string}): {line:string}', {
        day: fmtTrackerDate(since.standupOn, tracker.today),
        line: since.line,
      })
    : intl('This week: {line:string}', { line: tracker.week });
  const copyText = since
    ? intl('Since your last standup: {line:string}', { line: since.line })
    : shown;

  function copy() {
    onCopy?.(copyText);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPIED_MS);
  }

  return (
    <div class={boxClass}>
      <span class="min-w-0">{shown}</span>
      {onCopy && (
        <BtnSecondary class="min-h-11 shrink-0 bg-white dark:bg-gray-800" onClick={copy}>
          {copied ? intl('Copied') : intl('Copy into my answer')}
        </BtnSecondary>
      )}
    </div>
  );
}

export function TodaysCheckinCard({
  courseId,
  settings,
  localDate,
  isActiveDay,
  today,
  onSaved,
  onSubmitted,
  compact,
  collapseWhenSaved,
  date,
  tracker,
  trackerHref,
}: Props) {
  const intl = useIntl();
  const checkinDate = date || localDate;
  const isToday = checkinDate === localDate;
  const [answers, setAnswers] = useState(() => initialAnswers(settings, today));
  const [checkin, setCheckin] = useState<Checkin | undefined>(today);
  const [isSaving, setIsSaving] = useState(false);
  const [draftStatus, setDraftStatus] = useState<
    'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'submittedElsewhere'
  >('idle');
  const [isExpanded, setIsExpanded] = useState(!today || today.isDraft);
  const savedAnswersRef = useRef(answers);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const checkinRef = useRef(checkin);
  const firstAnswerRef = useRef<HTMLTextAreaElement>(null);

  // Debounced autosave: schedule a draft save a little after the student
  // stops typing, and only when the text actually changed since the last
  // save (so switching away and back doesn't re-save on mount).
  useEffect(() => {
    if (answers === savedAnswersRef.current) {
      return;
    }
    setDraftStatus('pending');
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    if (checkinRef.current?.submittedAt) {
      return;
    }
    autosaveTimerRef.current = setTimeout(() => {
      saveDraft();
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [answers]);

  if (!isActiveDay) {
    return;
  }

  function updateCheckin(next: Checkin) {
    checkinRef.current = next;
    setCheckin(next);
  }

  function buildAnswers() {
    return settings.prompts.map((p) => ({ promptId: p.id, answer: answers[p.id] || '' }));
  }

  async function saveDraft() {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = undefined;
    }
    if (checkinRef.current?.submittedAt || !hasAnyText(answers) || isSaving) {
      return;
    }
    savedAnswersRef.current = answers;
    setDraftStatus('saving');
    try {
      const saved = await rpx.checkins.saveCheckinDraft({
        courseId,
        checkinDate,
        answers: buildAnswers(),
      });
      if (checkinRef.current?.submittedAt) {
        return;
      }
      if (saved?.submittedAt) {
        updateCheckin(saved);
        onSaved(saved);
        setDraftStatus('submittedElsewhere');
        return;
      }
      if (saved) {
        updateCheckin(saved);
        onSaved(saved);
      }
      setDraftStatus('saved');
    } catch {
      // Best-effort: autosave failures stay silent so typing is never
      // interrupted. The manual Submit/Save Draft button still surfaces
      // real errors, and nothing typed is lost — it's still in the field.
      setDraftStatus('error');
    }
  }

  async function submit() {
    if (isSaving) {
      return;
    }
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = undefined;
    }
    setIsSaving(true);
    const wasSubmitted = !!checkin?.submittedAt;
    try {
      const saved = await rpx.checkins.submitCheckin({
        courseId,
        checkinDate,
        answers: buildAnswers(),
      });
      savedAnswersRef.current = answers;
      updateCheckin(saved);
      setDraftStatus('saved');
      onSaved(saved);
      const savedPromptIds = new Set(saved.answers.map((a) => a.promptId));
      const dropped = settings.prompts.some(
        (p) => (answers[p.id] || '').trim() && !savedPromptIds.has(p.id),
      );
      if (dropped) {
        showToast({
          type: 'warn',
          title: intl('Some answers were not saved'),
          message: intl(
            'The standup questions have changed. Refresh the page to see the current ones.',
          ),
        });
        return;
      }
      setIsExpanded(false);
      showToast({
        type: 'ok',
        title: wasSubmitted ? intl('Standup updated') : intl('Standup saved'),
        message: wasSubmitted
          ? intl('Your answers have been updated.')
          : intl('Thanks for submitting your standup.'),
      });
      onSubmitted?.(saved);
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  // Goes through setAnswers like typing does, so autosave picks it up.
  function copyIntoFirstAnswer(text: string) {
    const first = settings.prompts[0];
    if (!first) {
      return;
    }
    setAnswers((x) => {
      const current = x[first.id] || '';
      if (current.includes(text)) {
        return x;
      }
      return {
        ...x,
        [first.id]: current.trim() ? `${current.replace(/\s+$/, '')}\n${text}` : text,
      };
    });
    requestAnimationFrame(() => {
      const el = firstAnswerRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }

  const submittedAt = checkin?.submittedAt;
  const dayLabel = isToday
    ? intl("Today's standup")
    : intl('Standup for {day:string}', { day: dayjs(checkinDate).format('dddd, MMM D') });

  if (collapseWhenSaved && submittedAt && !isExpanded) {
    return (
      <section
        class={`flex items-center justify-between gap-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 ${
          compact ? 'p-4' : 'p-6'
        }`}
      >
        <div class="flex items-center gap-3">
          <span class="flex items-center justify-center size-8 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
            <IcoCheck class="size-4" />
          </span>
          <div class="flex flex-col">
            <h2 class="font-medium text-gray-900 dark:text-gray-100">
              {isToday ? intl("Today's standup is done") : intl('Standup submitted')}
            </h2>
            <span class="text-sm text-gray-500">
              {intl('Submitted at')} {dayjs(submittedAt).format('h:mm A')}
              {isToday && <> · {intl('you can edit until the end of the day')}</>}
            </span>
          </div>
        </div>
        <BtnSecondary onClick={() => setIsExpanded(true)}>{intl('Edit')}</BtnSecondary>
      </section>
    );
  }

  return (
    <section
      class={`border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 ${
        compact ? 'p-4' : 'p-6'
      }`}
    >
      <header class="flex flex-col mb-4">
        <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">{dayLabel}</h2>
        <span class="text-sm text-gray-500">{dayjs(checkinDate).format('dddd, MMM D')}</span>
      </header>
      {isToday && tracker && (!submittedAt || isExpanded) && (
        <TrackerSinceBox
          tracker={tracker}
          trackerHref={trackerHref}
          onCopy={settings.prompts.length ? copyIntoFirstAnswer : undefined}
        />
      )}
      <AsyncForm class="flex flex-col gap-4" onSubmit={submit}>
        {settings.prompts.map((prompt, index) => (
          <label key={prompt.id} class="flex flex-col gap-1">
            <span class="font-semibold text-gray-800 dark:text-gray-200">{prompt.text}</span>
            <textarea
              ref={index === 0 ? firstAnswerRef : undefined}
              class="ruz-input"
              rows={3}
              value={answers[prompt.id] || ''}
              onInput={(e) =>
                setAnswers((x) => ({ ...x, [prompt.id]: (e.target as HTMLTextAreaElement).value }))
              }
              onBlur={() => saveDraft()}
            />
          </label>
        ))}
        <footer class="flex items-center gap-4 flex-wrap">
          <BtnPrimary disabled={isSaving}>
            {submittedAt ? intl('Update standup') : intl('Submit standup')}
          </BtnPrimary>
          {!submittedAt && (
            <BtnSecondary type="button" disabled={isSaving} onClick={() => saveDraft()}>
              {intl('Save Draft')}
            </BtnSecondary>
          )}
          <span class="text-xs text-gray-500">
            {draftStatus === 'pending' && intl('Unsaved changes')}
            {draftStatus === 'saving' && intl('Saving draft…')}
            {draftStatus === 'saved' &&
              !submittedAt &&
              checkin?.updatedAt &&
              `${intl('Draft saved at')} ${dayjs(checkin.updatedAt).format('h:mm A')}`}
            {draftStatus === 'saved' &&
              submittedAt &&
              `${intl('Submitted at')} ${dayjs(submittedAt).format('h:mm A')}`}
            {draftStatus === 'error' && intl("Draft didn't save — try Save Draft again")}
            {draftStatus === 'submittedElsewhere' &&
              intl('Already submitted in another tab. Click Update standup to save these changes.')}
          </span>
        </footer>
      </AsyncForm>
    </section>
  );
}
